import { useState, useRef, useCallback, useEffect } from 'react';
import { whatsappApi } from '../../../api/whatsapp-api';

interface UseChatSearchProps {
    selectedContactId: string | null | undefined;
    onJumpToMessage: (messageId: string) => void;
}

interface SearchMatch {
    messageId: string;
    [key: string]: any;
}

export function useChatSearch({ selectedContactId, onJumpToMessage }: UseChatSearchProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchMatches, setSearchMatches] = useState<SearchMatch[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [totalSearchMatches, setTotalSearchMatches] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout>();

    const handleSearchMessages = useCallback(async (term: string) => {
        if (!term.trim() || !selectedContactId) {
            setSearchMatches([]);
            setTotalSearchMatches(0);
            setCurrentMatchIndex(0);
            return;
        }

        setIsSearching(true);
        try {
            const results = await whatsappApi.getSearchMessagesCursors(selectedContactId, { search: term.trim() });
            setSearchMatches(results.data || []);
            setTotalSearchMatches(results.total || 0);
            setCurrentMatchIndex(0);

            if (results.data && results.data.length > 0) {
                onJumpToMessage(results.data[0].messageId);
            }
        } catch (error) {
            console.error('Error searching messages:', error);
            setSearchMatches([]);
        } finally {
            setIsSearching(false);
        }
    }, [selectedContactId, onJumpToMessage]);

    const handleNextMatch = useCallback(() => {
        if (currentMatchIndex < searchMatches.length - 1) {
            const nextIndex = currentMatchIndex + 1;
            setCurrentMatchIndex(nextIndex);
            onJumpToMessage(searchMatches[nextIndex].messageId);
        }
    }, [currentMatchIndex, searchMatches, onJumpToMessage]);

    const handlePrevMatch = useCallback(() => {
        if (currentMatchIndex > 0) {
            const prevIndex = currentMatchIndex - 1;
            setCurrentMatchIndex(prevIndex);
            onJumpToMessage(searchMatches[prevIndex].messageId);
        }
    }, [currentMatchIndex, searchMatches, onJumpToMessage]);

    const debouncedSearchMessages = useCallback((term: string) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            handleSearchMessages(term);
        }, 300);
    }, [handleSearchMessages]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, []);

    return {
        searchTerm,
        setSearchTerm,
        searchMatches,
        currentMatchIndex,
        totalSearchMatches,
        isSearching,
        handleNextMatch,
        handlePrevMatch,
        debouncedSearchMessages,
    };
}
